import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ClipboardList, Send, CheckCircle2, Clock, Search as SearchIcon, RefreshCw } from "lucide-react";

const STATUS_PILL = {
  pending:     "bg-neutral-100 border-neutral-300 text-neutral-600",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  submitted:   "bg-emerald-50 border-emerald-300 text-emerald-800",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return iso;
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);   if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);   if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function ReactivationIntakes() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reactivation/intakes");
      setRows(data.intakes || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      (r.client_business_name || "").toLowerCase().includes(q) ||
      (r.client_email || "").toLowerCase().includes(q) ||
      (r.status || "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  const resend = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/reactivation/intakes/${id}/resend-link`);
      toast.success(`Magic link resent to ${data.email || "client"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const openLink = (token) => {
    const base = window.location.origin;
    const url = `${base}/reactivation-intake/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied."));
  };

  const stats = useMemo(() => {
    const pending    = rows.filter((r) => r.status === "pending").length;
    const inProgress = rows.filter((r) => r.status === "in_progress").length;
    const submitted  = rows.filter((r) => r.status === "submitted").length;
    return { pending, inProgress, submitted, total: rows.length };
  }, [rows]);

  return (
    <div className="p-10 max-w-7xl" data-testid="reactivation-intakes-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><ClipboardList className="w-3.5 h-3.5" /> Onboarding · Reactivation</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Reactivation intakes</h1>
          <p className="text-neutral-500 max-w-2xl text-sm">
            Every intake fired when a Database Reactivation campaign was created. Track who's filled it out, who's still stuck, and resend the magic link with one click.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/reactivation" className="lf-btn-outline text-sm inline-flex items-center gap-2">← Campaigns</Link>
          <button onClick={load} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="intakes-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <KpiTile label="Total intakes"   value={stats.total}      icon={ClipboardList} />
        <KpiTile label="Pending"         value={stats.pending}    icon={Clock}         accent="neutral" />
        <KpiTile label="In progress"     value={stats.inProgress} icon={Send}          accent="amber" />
        <KpiTile label="Submitted"       value={stats.submitted}  icon={CheckCircle2}  accent="emerald" />
      </div>

      {/* Search */}
      <div className="mb-6 relative max-w-md">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          data-testid="intakes-search"
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by client, email, or status…"
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 bg-white focus:outline-none focus:border-black text-sm"
        />
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading intakes…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center bg-white" data-testid="intakes-empty">
          <div className="font-display text-2xl mb-2">No intakes yet</div>
          <p className="text-neutral-500 mb-5">They appear here automatically when a reactivation campaign is created.</p>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">CSV</th>
                <th className="text-right px-4 py-3">Emails sent</th>
                <th className="text-left px-4 py-3">Last email</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`intake-row-${r.id.slice(0,8)}`}>
                  <td className="px-4 py-3">
                    <Link to={`/clients/${r.client_id}`} className="font-medium hover:underline">{r.client_business_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-neutral-500">{r.client_email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 border ${STATUS_PILL[r.status] || STATUS_PILL.pending}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.csv_uploaded
                      ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.csv_summary?.kept ?? "yes"} rows</span>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{r.email_send_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{timeAgo(r.last_email_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button onClick={() => openLink(r.token)} className="text-xs text-neutral-600 hover:text-neutral-900" data-testid={`intake-copy-${r.id.slice(0,8)}`}>Copy link</button>
                      <button
                        onClick={() => resend(r.id)}
                        disabled={busyId === r.id || r.status === "submitted"}
                        className="text-xs text-coral hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1"
                        data-testid={`intake-resend-${r.id.slice(0,8)}`}
                      >
                        <Send className="w-3 h-3" /> {busyId === r.id ? "…" : "Resend link"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, accent }) {
  const map = {
    neutral: "border-neutral-200 bg-white",
    amber:   "border-amber-200 bg-amber-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
  };
  return (
    <div className={`border ${map[accent] || "border-neutral-200 bg-white"} p-4`} data-testid={`intakes-kpi-${label.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-neutral-500">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value}</div>
    </div>
  );
}
